import React, { useState, useEffect, memo } from "react";
import { DatePicker, Button, Spin, Skeleton, message, Space, Select, Segmented } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from "recharts";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { getCategoryDistribution, getCategoryDistributionTrend } from "../../api/api";
import type { CategoryDistributionResponse, CategoryDistributionTrendResponse } from "../../types/api-models";

const { RangePicker } = DatePicker;

type AggregationType = 'daily' | 'weekly' | 'monthly';

interface CategoryDistributionProps {
  isExpanded?: boolean;
  dashboardData?: any;
}

const CategoryDistribution: React.FC<CategoryDistributionProps> = ({ isExpanded = false, dashboardData }) => {
  const [loading, setLoading] = useState(false);
  const [aggregateData, setAggregateData] = useState<CategoryDistributionResponse | null>(null);
  const [trendData, setTrendData] = useState<CategoryDistributionTrendResponse | null>(null);
  const [aggregation, setAggregation] = useState<AggregationType>('daily');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  const [categoryLimit, setCategoryLimit] = useState<number | 'all'>(5);


  useEffect(() => {
    // Skip initial fetch in expanded mode
    if (isExpanded) return;
    
    // For collapsed view, use dashboard data
    if (dashboardData?.category_distribution) {
      setAggregateData(dashboardData.category_distribution);
      setTrendData(null);
    }
  }, [isExpanded, dashboardData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (aggregation === 'daily') {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
          setLoading(false);
          return;
        }
        const from_date = dateRange[0].format('YYYY-MM-DD');
        const to_date = dateRange[1].format('YYYY-MM-DD');
        
        const response = await getCategoryDistribution(from_date, to_date);
        setAggregateData(response);
        setTrendData(null);
      } else {
        const response = await getCategoryDistributionTrend(
          aggregation as 'weekly' | 'monthly',
          typeof categoryLimit === 'number' ? categoryLimit : 5
        );
        setTrendData(response);
        setAggregateData(null);
      }
    } catch (error) {
      message.error('Failed to load category distribution');
      setAggregateData(null);
      setTrendData(null);
    } finally {
      setLoading(false);
    }
  };

  // Skeleton loader for collapsed view
  if (loading && !isExpanded) {
    return (
      <div style={{ padding: '16px' }}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  // Show loading spinner
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" tip="Loading category distribution..." />
      </div>
    );
  }

  // Show empty state with helpful message
  const hasData = (aggregateData && aggregateData.categories.length > 0) || (trendData && trendData.data_points.length > 0);
  
  const emptyStateMessage = !hasData && isExpanded ? (
    <div style={{ textAlign: 'center', padding: 60, color: '#666' }}>
      <p style={{ fontSize: 16, marginBottom: 8 }}>Ready to view category distribution</p>
      <p style={{ fontSize: 13, color: '#999' }}>Adjust filters above and click "Apply Filters" to view data</p>
    </div>
  ) : !hasData ? (
    <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
      No category data available
    </div>
  ) : null;

  // Render aggregate view (daily) - horizontal bar chart
  const renderAggregateView = () => {
    if (!aggregateData || aggregateData.categories.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 40, color: '#999', minHeight: isExpanded ? 300 : 200 }}>
          No category data available for the selected date range
        </div>
      );
    }

    // Apply limit filter
    const displayCategories = categoryLimit === 'all' 
      ? aggregateData.categories 
      : aggregateData.categories.slice(0, isExpanded ? categoryLimit as number : 5);

    // Color palette
    const colors = [
      '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1', 
      '#a4de6c', '#d084d0', '#8a89a6', '#ffbb28', '#ff6b6b',
      '#4ecdc4', '#45b7d1', '#96ceb4', '#74b9ff', '#a29bfe'
    ];
    
    const chartData = displayCategories.map((item, index) => ({
      category: item.category,
      count: item.count,
      pct: item.pct,
      fill: colors[index % colors.length],
    }));

    return (
      <div style={{ minHeight: isExpanded ? 350 : 200 }}>
        <div style={{ height: isExpanded ? 350 : 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={chartData} 
              layout="vertical"
              margin={{ top: 5, right: 30, left: 130, bottom: 5 }}
            >
            <XAxis type="number" />
            <YAxis 
              type="category" 
              dataKey="category" 
              width={120}
              tick={{ fontSize: 12 }}
            />
            <Tooltip 
              formatter={(value: number, name: string, props: any) => [
                `${value} citations (${props.payload.pct.toFixed(1)}%)`,
                'Count'
              ]}
              contentStyle={{ fontSize: 12 }}
            />
            <Bar dataKey="count" isAnimationActive>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Render trend view (weekly/monthly) - line chart
  const renderTrendView = () => {
    if (!trendData || trendData.data_points.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 40, color: '#999', minHeight: isExpanded ? 300 : 200 }}>
          No category trend data available
        </div>
      );
    }

    // Transform data for line chart
    const chartData = trendData.data_points.map(point => {
      const dataPoint: any = {
        period: dayjs(point.period_start).format(aggregation === 'weekly' ? 'MMM D' : 'MMM YYYY')
      };
      
      // Add each category as a separate data series
      point.categories.forEach(cat => {
        dataPoint[cat.category] = cat.count;
      });
      
      return dataPoint;
    });

    // Color palette for lines
    const colors = [
      '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1', 
      '#a4de6c', '#d084d0', '#8a89a6', '#ffbb28', '#ff6b6b'
    ];

    return (
      <div style={{ minHeight: isExpanded ? 350 : 200 }}>
        <div style={{ height: isExpanded ? 350 : 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="period" 
              tick={{ fontSize: 11 }}
              angle={aggregation === 'weekly' ? -45 : 0}
              textAnchor={aggregation === 'weekly' ? 'end' : 'middle'}
              height={60}
            />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip 
              contentStyle={{ fontSize: 12 }}
              cursor={{ fill: 'rgba(0,0,0,0.05)' }}
            />
            <Legend />
            {trendData.top_categories.map((category, index) => (
              <Bar
                key={category}
                dataKey={category}
                stackId="a"
                fill={colors[index % colors.length]}
                name={category}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
        </div>
        
        {/* Show insights */}
        {trendData.insights.length > 0 && (
          <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: '#333' }}>
              Key Insights:
            </div>
            {trendData.insights.map((insight, idx) => (
              <div key={idx} style={{ fontSize: 11, marginBottom: 4, color: '#666' }}>
                • {insight}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const chartComponent = hasData ? (
    aggregation === 'daily' ? renderAggregateView() : renderTrendView()
  ) : emptyStateMessage;

  // If not expanded, return chart only
  if (!isExpanded) {
    return chartComponent;
  }

  // Expanded view with controls
  return (
    <div>
      {/* Controls Row */}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Space wrap>
          <span style={{ fontSize: 13, color: '#666' }}>Aggregation:</span>
          <Segmented
            value={aggregation}
            onChange={(value) => setAggregation(value as AggregationType)}
            options={[
              { label: 'Daily', value: 'daily' },
              { label: 'Weekly', value: 'weekly' },
              { label: 'Monthly', value: 'monthly' }
            ]}
          />
          {aggregation === 'daily' ? (
            <>
              <span style={{ fontSize: 13, color: '#666', marginLeft: 16 }}>Date Range:</span>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  }
                }}
                format="YYYY-MM-DD"
                style={{ width: 260 }}
                disabledDate={(current) => {
                  if (!current) return false;
                  return current.isAfter(dayjs().endOf('day'));
                }}
              />
            </>
          ) : (
            <span style={{ fontSize: 13, color: '#666', marginLeft: 16, fontStyle: 'italic' }}>
              Showing last {aggregation === 'weekly' ? '52 weeks' : '12 months'}
            </span>
          )}
          
          <Select
            value={categoryLimit}
            onChange={setCategoryLimit}
            style={{ width: 120 }}
            options={[
              { label: 'Top 5', value: 5 },
              { label: 'Top 10', value: 10 },
              { label: 'Top 15', value: 15 },
              { label: 'All', value: 'all' },
            ]}
          />
        </Space>
        
        <Space>
          <Button type="primary" onClick={fetchData} loading={loading}>
            Apply Filters
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchData} disabled={!hasData}>
            Refresh
          </Button>
        </Space>
      </Space>

      {/* Chart */}
      {hasData ? chartComponent : emptyStateMessage}
      
      {/* Info footer */}
      {hasData && (
        <div style={{ marginTop: 16, fontSize: 12, color: '#999', textAlign: 'center' }}>
          {aggregateData && aggregateData.total_citations > 0 && (
            <>
              Showing {aggregateData.categories.length} categories from {aggregateData.total_citations.toLocaleString()} total citations • 
              Aggregation: {aggregateData.aggregation_used} • 
              Date range: {dayjs(aggregateData.date_range.from).format('MMM D, YYYY')} - {dayjs(aggregateData.date_range.to).format('MMM D, YYYY')}
            </>
          )}
          {trendData && trendData.data_points.length > 0 && (
            <>
              Showing {trendData.data_points.length} {aggregation} periods • 
              Tracking {trendData.top_categories.length} top categories • 
              Date range: {dayjs(trendData.date_range.from).format('MMM D, YYYY')} - {dayjs(trendData.date_range.to).format('MMM D, YYYY')}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const MemoizedCategoryDistribution = memo(CategoryDistribution);
MemoizedCategoryDistribution.displayName = 'CategoryDistribution';

export default MemoizedCategoryDistribution;

